import { reactionKey, type Reaction, type RoomId } from '@engineer-os/chat';
import type { ChatRow, RoomView } from './types';
const MAX_ROWS = 400;
/** One room's socket and the mutable state behind the snapshot the panel sees. */
export interface Link {
  room: RoomId;
  isLevel: boolean;
  /** The immutable snapshot. Replaced whole on every change; never edited in place. */
  view: RoomView;
  ws: WebSocket | null;
  attempt: number;
  retry: number | null;
  ping: number | null;
  /** Closed for good: the room was dropped, the hook unmounted, or the tag is banned here. */
  done: boolean;
  /** A permalink waiting for the welcome that lets it be asked for. */
  wantContext: number | null;
}
/** A room with a socket that has not said anything yet, and the shape `active` falls back to. */
export function blankView(room: RoomId, isLevel: boolean): RoomView {
  return {
    room,
    status: 'idle',
    you: null,
    members: [],
    messages: [],
    pins: [],
    hasEarlier: false,
    loadingEarlier: false,
    unread: 0,
    mentions: 0,
    full: false,
    isLevel,
    banned: null,
  };
}

/** Keep the newest MAX_ROWS, and remember that whatever fell off the front is still on the server. */
export function keepNewest(rows: ChatRow[]): Pick<RoomView, 'messages'> | Pick<RoomView, 'messages' | 'hasEarlier'> {
  if (rows.length <= MAX_ROWS) return { messages: rows };
  return { messages: rows.slice(rows.length - MAX_ROWS), hasEarlier: true };
}

/**
 * One bucket of one message, replaced in place.
 *
 * In place rather than removed and appended, because the row is a line of
 * buttons the reader is aiming at: a bucket that jumped to the end of the row
 * every time somebody else pressed it would move the target under the finger
 * of the person about to press it. An empty `tags` is the last person taking
 * theirs off, and the bucket goes.
 */
export function withReaction(rows: ChatRow[], id: number, emoji: string, tags: string[]): ChatRow[] {
  let hit = false;
  const next = rows.map((r) => {
    if (r.id !== id) return r;
    hit = true;
    const key = reactionKey(emoji);
    const buckets = r.reactions ?? [];
    const at = buckets.findIndex((b) => reactionKey(b.emoji) === key);
    let reactions: Reaction[];
    if (at === -1) reactions = tags.length ? [...buckets, { emoji, tags }] : buckets;
    else if (tags.length) reactions = buckets.map((b, i) => (i === at ? { emoji, tags } : b));
    else reactions = buckets.filter((_, i) => i !== at);
    return { ...r, reactions: reactions.length ? reactions : undefined };
  });
  return hit ? next : rows;
}
